import { EAppConfigOrganizationType, EPhysiciansTitle } from 'common/types'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useGetAppConfig } from 'lib/apollo/hooks'
import { createContext, ReactNode, useContext, useMemo } from 'react'

export const ORGANIZATIONS = {
  pcoms: 'org_G2pgB4ADsuWZ2aNK',
  medgeo: 'org_OM4vswifgUpIYcu1',
  adventist: 'org_GR1VqYoVzjaaiJns',
  ridgewood: 'org_t05y14V3XiElOuJI',
  bch: 'org_GSlAP1Xp1zyuKuwW',
}

type TOrganizationSettingsProviderProps = { children: ReactNode }

type TOrganizationContext = {
  physiciansTitle: EPhysiciansTitle
  showPhysiciansMobile: boolean
  showPhysiciansSpeciality: boolean
  showScrubTechnician: boolean
  enableSmsToReferringPhysicians: boolean
  enableConsumablesAutopopulate: boolean
  enableSpecialRequest: boolean
  enableDictation: boolean
  showPascPreferenceCardFeatures: boolean
  allowFractionalQuantities: boolean
  showGenerateGuestRepTokenTab: boolean
  enableAutoDetectAssetType: boolean
}

const OrganizationSettingsContext = createContext<
  TOrganizationContext | undefined
>(undefined)

const OrganizationSettingsProvider = ({
  children,
}: TOrganizationSettingsProviderProps) => {
  const { data: appConfigData } = useGetAppConfig()
  const organizationType =
    appConfigData?.getAppConfig?.type || EAppConfigOrganizationType.HOSPITAL
  const physiciansTitle =
    organizationType === EAppConfigOrganizationType.OMFS
      ? EPhysiciansTitle.ReferringPhysicians
      : EPhysiciansTitle.Physicians

  const showPhysiciansSpeciality = useFlags().showPhysiciansSpeciality || false
  const showPhysiciansMobile = useFlags().showPhysiciansMobile || false
  const showScrubTechnician = useFlags().showScrubTechnician || false
  const enableSmsToReferringPhysicians =
    useFlags().enableSmsToReferringPhysicians || false
  const enableConsumablesAutopopulate =
    useFlags().enableConsumablesAutopopulate || false
  const enableSpecialRequest = useFlags().enableSpecialRequest || false
  const enableDictation = useFlags().enableDictation || false
  const showPascPreferenceCardFeatures = Boolean(
    useFlags().showPascPreferenceCardFeatures
  )
  const allowFractionalQuantities =
    useFlags().allowFractionalQuantities || false
  const showGenerateGuestRepTokenTab =
    useFlags().showGenerateGuestRepTokenTab || false
  const cancelSurgeryAction = useFlags().cancelSurgeryAction || false
  const enableAutoDetectAssetType =
    useFlags().enableAutoDetectAssetType || false

  const value = useMemo(
    () => ({
      showPhysiciansSpeciality,
      physiciansTitle,
      showPhysiciansMobile,
      showScrubTechnician,
      enableSmsToReferringPhysicians,
      enableConsumablesAutopopulate,
      enableSpecialRequest,
      enableDictation,
      showPascPreferenceCardFeatures,
      allowFractionalQuantities,
      showGenerateGuestRepTokenTab,
      cancelSurgeryAction,
      enableAutoDetectAssetType,
    }),
    [
      physiciansTitle,
      showPhysiciansSpeciality,
      showPhysiciansMobile,
      showScrubTechnician,
      enableSmsToReferringPhysicians,
      enableConsumablesAutopopulate,
      enableSpecialRequest,
      enableDictation,
      showPascPreferenceCardFeatures,
      allowFractionalQuantities,
      showGenerateGuestRepTokenTab,
      cancelSurgeryAction,
      enableAutoDetectAssetType,
    ]
  )

  return (
    <OrganizationSettingsContext.Provider value={value}>
      {children}
    </OrganizationSettingsContext.Provider>
  )
}

const useOrganizationSettings = () => {
  const context = useContext(OrganizationSettingsContext)

  if (context === undefined)
    throw new Error(
      'useOrganizationSettings must be used within OrganizationSettingsProvider'
    )

  return context
}

export { OrganizationSettingsProvider, useOrganizationSettings }
